import { useId, useState, type FormEvent } from 'react'
import { CITIES, HELLO_EMAIL, WAITLIST_ENDPOINT } from '../../lib/content'
import { clean, EMAIL_RE, submitForm } from '../../lib/submit'
import { ActivityIcon } from '../ui/Icon'
import { LogoMark } from '../ui/Logo'
import { Reveal } from '../ui/Reveal'
import { SmartLink } from '../ui/SmartLink'
import './Waitlist.css'

interface Props { city: string; onCityChange: (c: string) => void }

export function Waitlist({ city, onCityChange }: Props) {
  const id = useId()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'api' | 'email'>('idle')
  const [doneFor, setDoneFor] = useState('')
  const [trap, setTrap] = useState('')

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (status === 'sending') return
    const v = clean(email, 254)
    // Bots fill the hidden field; people never see it.
    if (trap) { setDoneFor(city); setStatus('api'); return }
    if (!EMAIL_RE.test(v)) {
      setError('Enter an email like you@example.com.')
      document.getElementById(`${id}-email`)?.focus()
      return
    }
    setError('')
    setStatus('sending')
    const res = await submitForm(WAITLIST_ENDPOINT, { form: 'waitlist', email: v, city, source: 'landing-waitlist', page: window.location.pathname }, {
      to: HELLO_EMAIL, subject: `Waitlist: ${city}`, body: `Please add me to the mauj waitlist.\n\nEmail: ${v}\nCity: ${city}`,
    })
    if (res.ok) {
      setDoneFor(city)
      setStatus(res.via)
      if (res.via === 'api') setEmail('')
    } else {
      setStatus('idle')
      setError(res.message)
    }
  }

  return (
    <section id="waitlist">
      <div className="wrap">
        <Reveal className="wl glass amber">
          <div className="min0">
            <div className="eyebrow">Waitlist</div>
            <h2 className="wl-title">Your city has <em className="s">plans for you.</em></h2>
            <p>The iOS and Android apps are on their way. Join the waitlist and we'll email you once, when mauj opens in your city. For adults 18 and over.</p>
            <form className="form" noValidate onSubmit={submit}>
              <div className="field">
                <label htmlFor={`${id}-email`}>Email</label>
                <input id={`${id}-email`} type="email" inputMode="email" maxLength={254} spellCheck={false} autoCapitalize="none" placeholder="you@example.com" autoComplete="email" value={email} aria-invalid={!!error} aria-describedby={`${id}-err`}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError('') }} />
              </div>
              <div className="field city-field">
                <label htmlFor={`${id}-city`}>City</label>
                <select id={`${id}-city`} value={city} onChange={(e) => onCityChange(e.target.value)}>
                  {CITIES.map((c) => <option key={c}>{c}</option>)}
                  <option>Somewhere else</option>
                </select>
              </div>
              <div className="hp" aria-hidden="true"><label htmlFor={`${id}-website`}>Website</label><input id={`${id}-website`} tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} /></div>
              <button className="btn gold" type="submit" disabled={status === 'sending'}>{status === 'sending' ? 'Joining…' : 'Join the waitlist'}</button>
            </form>
            <div className="err" id={`${id}-err`} aria-live="polite">{error}</div>
            <p className="consent">By joining, you agree to our <SmartLink to="/privacy">privacy policy</SmartLink>. We'll only email you about mauj opening in your city.</p>
            {(status === 'api' || status === 'email') && (
              <div className="done glass thin" role="status">
                <span className="ico"><ActivityIcon name="chai" /></span>
                <div>
                  <b>{status === 'api' ? `You're on the list for ${doneFor}.` : 'Almost there.'}</b>
                  <div className="muted small">{status === 'api' ? "We'll email you once, when your city opens." : `Your email app should open with the request ready to send. If it didn't, write to ${HELLO_EMAIL}.`}</div>
                </div>
              </div>
            )}
          </div>
          <div className="bigicon" aria-hidden="true"><LogoMark variant="ring" className="bigmark" /></div>
        </Reveal>
      </div>
    </section>
  )
}
